import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';

import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';

import type { Id } from '@e-socna/data-layer';

import { DirectorResultRow } from './DirectorResultRow';
import type { DirectorSettledOutcome } from './hooks/useDirectorPersonCreation.hook';
import type { ActiveMembership } from './types/active-membership.type';
import type { OrganizationSearchDirectorPersonnePhysique } from './types/organization-search-result.type';

export interface ArmedDirector {
  readonly director: OrganizationSearchDirectorPersonnePhysique;
  readonly isSelected: boolean;
}

interface Props {
  readonly armedDirectors: readonly ArmedDirector[];
  readonly fallbackCivilityId: Id;
  readonly onActiveMembershipsChange: (
    activeMemberships: readonly ActiveMembership[],
  ) => void;
  readonly onPendingChange: (hasPending: boolean) => void;
}

export const DirectorsResultsList = ({
  armedDirectors,
  fallbackCivilityId,
  onActiveMembershipsChange,
  onPendingChange,
}: Readonly<Props>) => {
  const { enqueueSnackbar } = useSnackbar();

  const armedCount = useMemo(
    () => armedDirectors.filter((entry) => entry.isSelected).length,
    [armedDirectors],
  );

  const [settledOutcomes, setSettledOutcomes] = useState<
    ReadonlyMap<number, DirectorSettledOutcome>
  >(new Map());

  const handleRowSettled = useCallback(
    (index: number, outcome: Readonly<DirectorSettledOutcome>) => {
      setSettledOutcomes((previous) => {
        const next = new Map(previous);
        next.set(index, outcome);
        return next;
      });
    },
    [],
  );

  useEffect(() => {
    onPendingChange(armedCount > 0 && settledOutcomes.size < armedCount);
  }, [armedCount, onPendingChange, settledOutcomes.size]);

  useEffect(() => {
    onActiveMembershipsChange(
      Array.from(settledOutcomes.values())
        .map((outcome) => outcome.membership)
        .filter((membership): membership is ActiveMembership => !!membership),
    );
  }, [onActiveMembershipsChange, settledOutcomes]);

  const hasToastedRef = useRef(false);

  useEffect(() => {
    if (
      hasToastedRef.current ||
      armedCount === 0 ||
      settledOutcomes.size < armedCount
    ) {
      return;
    }

    hasToastedRef.current = true;

    const failedCount = Array.from(settledOutcomes.values()).filter(
      (outcome) => outcome.status === 'rejected',
    ).length;

    enqueueSnackbar(
      failedCount > 0
        ? "Au moins un dirigeant n'a pas pu être créé (voir le détail ligne par ligne)."
        : 'Tous les dirigeants sélectionnés ont été créés.',
      { variant: failedCount > 0 ? 'error' : 'success' },
    );
  }, [armedCount, enqueueSnackbar, settledOutcomes]);

  return (
    <Stack divider={<Divider />} spacing={1}>
      {armedDirectors.map((entry, index) => (
        <DirectorResultRow
          canStart={entry.isSelected}
          director={entry.director}
          fallbackCivilityId={fallbackCivilityId}
          index={index}
          isSelected={entry.isSelected}
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          onSettled={handleRowSettled}
        />
      ))}
    </Stack>
  );
};
